/* fs-ats-report.js
 * Gated WeAmerican ATS report (sections/fs-ats-report.liquid).
 * Data source: Storefront API (public token on the Full Story Utilities channel, passed
 * via data-sf-token) — returns EVERY active product irrespective of sales channel, so
 * off-price products that aren't published to the Online Store are included. Cost comes
 * from the storefront-exposed custom.cost metafield; quantities from quantityAvailable
 * (available-to-sell across locations). Gift cards / service products (untracked
 * inventory) are excluded — they're unpublished from the FSU channel and filtered here.
 * Fallback: if the API fails, re-fetch the Liquid paginate pages exactly like the old
 * version (online-store products only) and parse the server-rendered rows.
 * Flow: SHA-256 password gate -> load model -> render with filters / sort /
 * group-by-style / live totals / CSV export. The gate is obscurity, not security:
 * token and data are visible in page source.
 *
 * Columns: Cost|Price|Title|Image|Material|Color|XS|S|M|L|XL|XXL|o/s|Other|Variants|Total.
 * XXL + Other columns (and their filter options) are injected client-side so the section
 * markup stays untouched; "Other" catches non-standard sizes (numeric, ranges) and shows
 * the size:qty breakdown on hover. Group-by-style groups by product id (data-pid), so two
 * distinct products that share a title remain separate groups with their own cost/price.
 */
(function () {
  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }
  function num(txt) {
    var t = (txt || '').replace(/[^0-9.\-]/g, '');
    if (t === '' || t === '-') return null;
    var n = parseFloat(t);
    return isNaN(n) ? null : n;
  }
  function intval(txt) { var n = parseInt((txt || '').replace(/[^0-9\-]/g, ''), 10); return isNaN(n) ? 0 : Math.max(0, n); }
  function sz(n) { return (n && n > 0) ? n : ''; }
  async function sha256Hex(str) {
    var buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf)).map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
  }
  function moneyFmt(amount, currency) {
    var n = parseFloat(amount);
    if (isNaN(n)) return '';
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(n); }
    catch (e) { return '$' + n.toFixed(2); }
  }
  // normalise a size option value the same way fs-ats-row.liquid does:
  // lowercase, trim, drop spaces & hyphens (keep "/" so ranges stay distinct)
  function sizeKey(osize) {
    var v = String(osize == null ? '' : osize).toLowerCase().trim().replace(/ /g, '').replace(/-/g, '');
    if (v === 'xs' || v === 'xsmall' || v === 'extrasmall') return 'xs';
    if (v === 's' || v === 'small') return 'sm';
    if (v === 'm' || v === 'md' || v === 'med' || v === 'medium') return 'md';
    if (v === 'l' || v === 'large') return 'lg';
    if (v === 'xl' || v === 'xlarge' || v === 'extralarge') return 'xl';
    if (v === 'xxl' || v === '2xl' || v === '2x' || v === 'xxlarge' || v === '2xlarge') return 'xxl';
    if (v === 'os' || v === 'o/s' || v === 'onesize' || v === 'onesizefitsall' || v === 'osfa' || v === 'o' || v === 'os1') return 'os';
    return 'other';
  }

  var SIZE_KEYS = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', 'os', 'other'];
  var LOW = 10; // total <= LOW (and > 0) flagged low-stock
  var SF_VERSION = '2026-04';
  var SF_PAGE = 50;
  // @inContext pins the primary (US) market so prices stay in base-shop USD instead of
  // being converted to the viewer's local market currency
  var SF_QUERY = 'query($cursor:String) @inContext(country:US) {products(first:' + SF_PAGE + ',after:$cursor,sortKey:TITLE){pageInfo{hasNextPage endCursor}nodes{id title vendor productType tags featuredImage{url} cost:metafield(namespace:"custom",key:"cost"){value} variants(first:100){nodes{quantityAvailable price{amount currencyCode} selectedOptions{name value} image{url}}}}}}';

  function init(root) {
    if (!root || root.dataset.atsInit) return;
    root.dataset.atsInit = '1';

    var expected = (root.dataset.hash || '').toLowerCase();
    var gate = root.querySelector('[data-ats-gate]');
    var report = root.querySelector('[data-ats-report]');
    var form = root.querySelector('[data-ats-form]');
    var input = root.querySelector('[data-ats-input]');
    var errorEl = root.querySelector('[data-ats-error]');
    var tbody = root.querySelector('[data-ats-tbody]');
    var table = root.querySelector('[data-ats-table]');
    var searchEl = root.querySelector('[data-ats-search]');
    var clearEl = root.querySelector('[data-ats-search-clear]');
    var fcolorEl = root.querySelector('[data-ats-fcolor]');
    var fsizeEl = root.querySelector('[data-ats-fsize]');
    var instockEl = root.querySelector('[data-ats-instock]');
    var groupEl = root.querySelector('[data-ats-group]');
    var expandEl = root.querySelector('[data-ats-expand]');
    var exportEl = root.querySelector('[data-ats-export]');
    var statusEl = root.querySelector('[data-ats-status]');
    var asofEl = root.querySelector('[data-ats-asof]');
    var progress = root.querySelector('[data-ats-progress]');
    var progressBar = root.querySelector('[data-ats-progress-bar]');
    var tpEl = root.querySelector('[data-ats-total-pages]');
    var totalPages = tpEl ? (parseInt(tpEl.getAttribute('data-ats-total-pages'), 10) || 1) : 1;

    // Ensure XXL + Other size columns & filter options exist (idempotent). Keeps the
    // section markup untouched while guaranteeing non-standard sizes are never hidden.
    (function ensureExtraCols() {
      if (table && !table.querySelector('thead th[data-sort="xxl"]')) {
        var xlTh = table.querySelector('thead th[data-sort="xl"]');
        if (xlTh) {
          var thx = document.createElement('th');
          thx.className = 'ats-size'; thx.setAttribute('data-sort', 'xxl'); thx.setAttribute('data-type', 'num');
          thx.innerHTML = 'XXL<span class="arrow"></span>';
          xlTh.parentNode.insertBefore(thx, xlTh.nextSibling);
        }
        var osTh = table.querySelector('thead th[data-sort="os"]');
        if (osTh) {
          var tho = document.createElement('th');
          tho.className = 'ats-size'; tho.setAttribute('data-sort', 'other'); tho.setAttribute('data-type', 'num');
          tho.innerHTML = 'Other<span class="arrow"></span>';
          osTh.parentNode.insertBefore(tho, osTh.nextSibling);
        }
      }
      if (fsizeEl && !fsizeEl.querySelector('option[value="xxl"]')) {
        var xlOpt = fsizeEl.querySelector('option[value="xl"]');
        var ox = document.createElement('option'); ox.value = 'xxl'; ox.textContent = 'XXL in stock';
        if (xlOpt) xlOpt.parentNode.insertBefore(ox, xlOpt.nextSibling); else fsizeEl.appendChild(ox);
        var oo = document.createElement('option'); oo.value = 'other'; oo.textContent = 'Other size in stock';
        fsizeEl.appendChild(oo);
      }
      var legendEl = root.querySelector('.fs-ats__legend');
      if (legendEl) legendEl.textContent = 'o/s = one size · Other = non-standard sizes (hover for breakdown) · quantities = units available to sell';
    })();

    var storeKey = 'fs-ats:' + expected;
    var model = [];
    var collapsed = {};   // group key -> true when collapsed
    var sortKey = 'title', sortDir = 'asc';
    var loaded = false;

    // Group key = product id when present (so same-title products stay separate),
    // falling back to lowercased title for any legacy row without data-pid.
    function gkey(r) { return (r.pid && r.pid.length) ? r.pid : (r.title || '').toLowerCase(); }

    function setStatus(msg) { if (statusEl) statusEl.textContent = msg; }

    function unlock() {
      if (gate) gate.style.display = 'none';
      if (report) report.hidden = false;
      if (!loaded) { loaded = true; loadAll(); }
    }
    try { if (expected && sessionStorage.getItem(storeKey) === '1') unlock(); } catch (e) {}

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        errorEl.textContent = '';
        var val = (input.value || '').trim();
        if (!val) { errorEl.textContent = 'Please enter the password.'; return; }
        if (!window.crypto || !crypto.subtle) { errorEl.textContent = 'Secure context required (use https).'; return; }
        sha256Hex(val).then(function (h) {
          if (h === expected) { try { sessionStorage.setItem(storeKey, '1'); } catch (e) {} unlock(); }
          else { errorEl.textContent = 'Incorrect password.'; input.select(); }
        });
      });
    }

    /* ---- build row objects for one Storefront API product (one row per color),
       mirroring the Liquid logic in snippets/fs-ats-row.liquid ---- */
    function productToRows(p) {
      var t = (p.title || '').toLowerCase();
      var pt = (p.productType || '').toLowerCase();
      if (pt === 'gift cards' || pt === 'e-gift cards' || pt === 'return' || t.indexOf('gift card') > -1 || t.indexOf('e-gift') > -1) return [];
      var variants = (p.variants && p.variants.nodes) || [];
      if (!variants.length) return [];

      var pid = String(p.id || '').replace(/^.*\//, '');
      var tagsStr = (p.tags || []).join(', ');
      var costNum = (p.cost && p.cost.value != null) ? parseFloat(p.cost.value) : null;
      if (costNum != null && isNaN(costNum)) costNum = null;

      // group variants by Color option (order-preserving); products without a color
      // option collapse into a single unnamed group
      var groups = {}, order = [];
      variants.forEach(function (v) {
        var color = '';
        (v.selectedOptions || []).forEach(function (o) {
          var n = (o.name || '').toLowerCase().trim();
          if (n === 'color' || n === 'colour') color = o.value || '';
        });
        var key = color || '__ALL__';
        if (!groups[key]) { groups[key] = []; order.push(key); }
        groups[key].push(v);
      });

      var rows = [];
      order.forEach(function (key) {
        var grp = groups[key];
        var agg = { xs: 0, sm: 0, md: 0, lg: 0, xl: 0, xxl: 0, os: 0, other: 0 };
        var otherDetail = '', total = 0, vars = 0, hasNeg = false;
        var priceNum = null, currency = 'USD', imgUrl = '';
        grp.forEach(function (v) {
          if (priceNum == null && v.price) { priceNum = parseFloat(v.price.amount); currency = v.price.currencyCode || currency; }
          if (!imgUrl && v.image && v.image.url) imgUrl = v.image.url;
          var q = v.quantityAvailable;
          if (q == null) return; // untracked inventory -> skip, mirrors inventory_management check
          if (q < 0) { hasNeg = true; q = 0; }
          total += q;
          if (q > 0) vars++;
          var osize = 'o/s';
          (v.selectedOptions || []).forEach(function (o) {
            var n = (o.name || '').toLowerCase().trim();
            if (n === 'size') osize = o.value || '';
          });
          var b = sizeKey(osize);
          agg[b] += q;
          if (b === 'other' && q > 0) otherDetail += osize + ':' + q + '  ';
        });
        // hide oversold-and-unavailable rows, mirrors the Liquid rule
        if (hasNeg && total === 0) return;
        if (!imgUrl && p.featuredImage) imgUrl = p.featuredImage.url || '';
        var src = imgUrl ? imgUrl + (imgUrl.indexOf('?') > -1 ? '&' : '?') + 'width=96' : '';
        rows.push({
          pid: pid,
          title: p.title || '',
          material: p.vendor || '',
          color: key === '__ALL__' ? '' : key,
          tags: tagsStr,
          costStr: costNum == null ? '' : moneyFmt(costNum, currency),
          priceStr: priceNum == null ? '' : moneyFmt(priceNum, currency),
          costNum: costNum,
          priceNum: priceNum,
          imgHtml: src ? '<img src="' + esc(src) + '" alt="' + esc(p.title || '') + '" width="48" height="64" loading="lazy">' : '',
          xs: agg.xs, sm: agg.sm, md: agg.md, lg: agg.lg, xl: agg.xl, xxl: agg.xxl, os: agg.os, other: agg.other,
          otherDetail: otherDetail.trim(),
          vars: vars,
          total: total
        });
      });
      return rows;
    }

    /* ---- primary loader: Storefront API pagination (all active products,
       channel-independent) ---- */
    async function loadStorefront() {
      var token = root.dataset.sfToken || '';
      if (!token) throw new Error('missing storefront token');
      var rows = [], cursor = null, page = 0;
      for (;;) {
        page++;
        if (page > 40) break; // hard stop (~2000 products)
        setStatus('Loading products ' + ((page - 1) * SF_PAGE + 1) + '+…');
        if (progressBar) progressBar.style.width = Math.min(92, Math.round(page / (page + 2) * 100)) + '%';
        var res = await fetch('/api/' + SF_VERSION + '/graphql.json', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': token },
          body: JSON.stringify({ query: SF_QUERY, variables: { cursor: cursor } })
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var json = await res.json();
        if (json.errors && json.errors.length) throw new Error(json.errors[0].message || 'GraphQL error');
        var prods = json.data && json.data.products;
        if (!prods) throw new Error('malformed response');
        prods.nodes.forEach(function (p) { rows.push.apply(rows, productToRows(p)); });
        if (!prods.pageInfo.hasNextPage) break;
        cursor = prods.pageInfo.endCursor;
      }
      return rows;
    }

    /* ---- fallback loader: re-fetch the Liquid paginate pages cache-busted and
       parse the server-rendered rows (Online-Store products only) ---- */
    async function loadLegacy() {
      var path = window.location.pathname, bust = Date.now();
      var holder = document.createElement('tbody');
      for (var p = 1; p <= totalPages; p++) {
        if (progressBar) progressBar.style.width = Math.round((p - 1) / totalPages * 100) + '%';
        setStatus('Loading ' + p + '/' + totalPages + '…');
        try {
          var res = await fetch(path + '?page=' + p + '&_ats=' + bust, { headers: { 'X-Requested-With': 'fetch' }, cache: 'no-store' });
          var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          doc.querySelectorAll('tr.ats-row').forEach(function (r) { holder.appendChild(document.importNode(r, true)); });
        } catch (err) { /* keep going */ }
      }
      // if fetches yielded nothing, use the server-rendered page 1 already in tbody
      var src = holder.querySelectorAll('tr.ats-row').length ? holder : tbody;
      return parseRows(src.querySelectorAll('tr.ats-row'));
    }

    async function loadAll() {
      if (progress) progress.hidden = false;
      var fromApi = false;
      try {
        model = await loadStorefront();
        fromApi = true;
      } catch (err) {
        model = await loadLegacy();
      }
      if (progressBar) progressBar.style.width = '100%';
      buildColorFilter();
      if (asofEl) asofEl.textContent = 'Data as of ' + new Date().toLocaleString() + (fromApi ? '' : ' — online-store products only (live API unavailable)');
      if (progress) progress.hidden = true;
      render();
    }

    function parseRows(rows) {
      var out = [];
      rows.forEach(function (tr) {
        var c = tr.children;
        out.push({
          pid: tr.getAttribute('data-pid') || '',
          title: tr.getAttribute('data-title') || (c[2] && c[2].textContent.trim()) || '',
          material: tr.getAttribute('data-material') || (c[4] && c[4].textContent.trim()) || '',
          color: tr.getAttribute('data-color') || (c[5] && c[5].textContent.trim()) || '',
          tags: tr.getAttribute('data-tags') || '',
          costStr: c[0] ? c[0].textContent.trim() : '',
          priceStr: c[1] ? c[1].textContent.trim() : '',
          costNum: c[0] ? num(c[0].textContent) : null,
          priceNum: c[1] ? num(c[1].textContent) : null,
          imgHtml: c[3] ? c[3].innerHTML.trim() : '',
          xs: c[6] ? intval(c[6].textContent) : 0,
          sm: c[7] ? intval(c[7].textContent) : 0,
          md: c[8] ? intval(c[8].textContent) : 0,
          lg: c[9] ? intval(c[9].textContent) : 0,
          xl: c[10] ? intval(c[10].textContent) : 0,
          xxl: c[11] ? intval(c[11].textContent) : 0,
          os: c[12] ? intval(c[12].textContent) : 0,
          other: c[13] ? intval(c[13].textContent) : 0,
          otherDetail: c[13] ? (c[13].getAttribute('title') || '') : '',
          vars: c[14] ? intval(c[14].textContent) : 0,
          total: c[15] ? intval(c[15].textContent) : 0
        });
      });
      return out;
    }

    function buildColorFilter() {
      if (!fcolorEl) return;
      var seen = {}, list = [];
      model.forEach(function (r) { var k = (r.color || '').trim(); if (k && !seen[k]) { seen[k] = 1; list.push(k); } });
      list.sort(function (a, b) { return a.localeCompare(b); });
      var html = '<option value="">All colors</option>';
      list.forEach(function (c) { html += '<option value="' + esc(c) + '">' + esc(c) + '</option>'; });
      fcolorEl.innerHTML = html;
    }

    /* ---- filtering ---- */
    function filtered() {
      var q = (searchEl && searchEl.value || '').trim().toLowerCase();
      var fc = fcolorEl ? fcolorEl.value : '';
      var fs = fsizeEl ? fsizeEl.value : '';
      var inStock = instockEl ? instockEl.checked : false;
      return model.filter(function (r) {
        if (q) { var hay = (r.title + ' ' + r.material + ' ' + r.color + ' ' + r.tags).toLowerCase(); if (hay.indexOf(q) === -1) return false; }
        if (fc && r.color !== fc) return false;
        if (fs && !(r[fs] > 0)) return false;
        if (inStock && !(r.total > 0)) return false;
        return true;
      });
    }

    function cmp(a, b) {
      var dir = sortDir === 'asc' ? 1 : -1;
      var th = table.querySelector('thead th[data-sort="' + sortKey + '"]');
      var numeric = th && th.dataset.type === 'num';
      var fld = sortKey === 'cost' ? 'costNum' : sortKey === 'price' ? 'priceNum' : sortKey;
      var av = a[fld], bv = b[fld];
      if (numeric) { av = (av == null ? -Infinity : av); bv = (bv == null ? -Infinity : bv); return (av - bv) * dir; }
      return String(av || '').localeCompare(String(bv || '')) * dir;
    }

    function totalClass(t) { return t === 0 ? ' is-zero' : (t <= LOW ? ' is-low' : ''); }

    function rowHtml(r, hidden) {
      return '<tr class="ats-row' + (hidden ? ' is-hidden' : '') + (r.total === 0 ? ' is-zero' : '') + '"'
        + ' data-pid="' + esc(r.pid) + '" data-title="' + esc(r.title) + '" data-material="' + esc(r.material) + '" data-color="' + esc(r.color) + '" data-tags="' + esc(r.tags) + '">'
        + '<td class="ats-num ats-cost">' + esc(r.costStr) + '</td>'
        + '<td class="ats-num">' + esc(r.priceStr) + '</td>'
        + '<td class="ats-title">' + esc(r.title) + '</td>'
        + '<td class="ats-img">' + r.imgHtml + '</td>'
        + '<td class="ats-material" title="' + esc(r.material) + '">' + esc(r.material) + '</td>'
        + '<td class="ats-color">' + esc(r.color) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.xs) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.sm) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.md) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.lg) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.xl) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.xxl) + '</td>'
        + '<td class="ats-num ats-size">' + sz(r.os) + '</td>'
        + '<td class="ats-num ats-size ats-other"' + (r.otherDetail ? ' title="' + esc(r.otherDetail) + '"' : '') + '>' + sz(r.other) + '</td>'
        + '<td class="ats-num">' + (r.vars || 0) + '</td>'
        + '<td class="ats-num ats-total' + totalClass(r.total) + '">' + r.total + '</td>'
        + '</tr>';
    }

    function groupHtml(key, rows) {
      var agg = { xs: 0, sm: 0, md: 0, lg: 0, xl: 0, xxl: 0, os: 0, other: 0, vars: 0, total: 0 };
      rows.forEach(function (r) { SIZE_KEYS.forEach(function (k) { agg[k] += r[k]; }); agg.vars += r.vars; agg.total += r.total; });
      var isCol = !!collapsed[key];
      var title = rows[0].title, material = rows[0].material;
      return '<tr class="ats-group' + (isCol ? ' is-collapsed' : '') + '" data-group="' + esc(key) + '">'
        + '<td class="ats-group-id" colspan="6"><span class="ats-caret">&#9662;</span> ' + esc(title)
        + ' <span class="ats-group-sub">&middot; ' + rows.length + ' color' + (rows.length === 1 ? '' : 's') + ' &middot; ' + esc(material) + '</span></td>'
        + '<td class="ats-num ats-size">' + sz(agg.xs) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.sm) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.md) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.lg) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.xl) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.xxl) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.os) + '</td>'
        + '<td class="ats-num ats-size">' + sz(agg.other) + '</td>'
        + '<td class="ats-num">' + agg.vars + '</td>'
        + '<td class="ats-num ats-total' + totalClass(agg.total) + '">' + agg.total + '</td>'
        + '</tr>';
    }

    function render() {
      var rows = filtered();
      var grouped = groupEl ? groupEl.checked : false;
      var html = '', styles = {}, units = 0;
      rows.forEach(function (r) { units += r.total; styles[r.title] = 1; });

      if (grouped) {
        var map = {}, order = [];
        rows.forEach(function (r) { var k = gkey(r); if (!map[k]) { map[k] = []; order.push(k); } map[k].push(r); });
        // keep groups alphabetical by title (same-title products land next to each other)
        order.sort(function (a, b) { var ta = (map[a][0].title || '').toLowerCase(), tb = (map[b][0].title || '').toLowerCase(); return ta < tb ? -1 : (ta > tb ? 1 : 0); });
        order.forEach(function (k) {
          var grp = map[k].slice().sort(cmp);
          html += groupHtml(k, grp);
          var hide = !!collapsed[k];
          grp.forEach(function (r) { html += rowHtml(r, hide); });
        });
      } else {
        rows.slice().sort(cmp).forEach(function (r) { html += rowHtml(r, false); });
      }
      tbody.innerHTML = html;
      setStatus(rows.length + ' colors · ' + Object.keys(styles).length + ' styles · ' + units.toLocaleString() + ' units');
    }

    /* ---- events ---- */
    if (searchEl) searchEl.addEventListener('input', function () { if (clearEl) clearEl.hidden = !searchEl.value; render(); });
    if (clearEl) clearEl.addEventListener('click', function () { searchEl.value = ''; clearEl.hidden = true; render(); searchEl.focus(); });
    if (fcolorEl) fcolorEl.addEventListener('change', render);
    if (fsizeEl) fsizeEl.addEventListener('change', render);
    if (instockEl) instockEl.addEventListener('change', render);
    if (groupEl) groupEl.addEventListener('change', function () { if (expandEl) expandEl.style.display = groupEl.checked ? '' : 'none'; render(); });

    if (expandEl) expandEl.addEventListener('click', function () {
      // if anything is collapsed -> expand all; else collapse all
      var anyCollapsed = Object.keys(collapsed).length > 0;
      collapsed = {};
      if (!anyCollapsed) { model.forEach(function (r) { collapsed[gkey(r)] = true; }); }
      expandEl.textContent = anyCollapsed ? 'Collapse all' : 'Expand all';
      render();
    });

    // toggle a single group via its header
    tbody.addEventListener('click', function (e) {
      var g = e.target.closest ? e.target.closest('tr.ats-group') : null;
      if (!g) return;
      var key = g.getAttribute('data-group');
      if (collapsed[key]) delete collapsed[key]; else collapsed[key] = true;
      g.classList.toggle('is-collapsed', !!collapsed[key]);
      var n = g.nextElementSibling;
      while (n && n.classList.contains('ats-row')) { n.classList.toggle('is-hidden', !!collapsed[key]); n = n.nextElementSibling; }
    });

    table.querySelectorAll('thead th[data-sort]').forEach(function (th) {
      th.addEventListener('click', function () {
        var k = th.getAttribute('data-sort');
        if (sortKey === k) { sortDir = sortDir === 'asc' ? 'desc' : 'asc'; } else { sortKey = k; sortDir = th.dataset.type === 'num' ? 'desc' : 'asc'; }
        table.querySelectorAll('thead th .arrow').forEach(function (a) { a.textContent = ''; });
        var arrow = th.querySelector('.arrow'); if (arrow) arrow.textContent = sortDir === 'asc' ? ' ▲' : ' ▼';
        render();
      });
    });

    /* ---- CSV export (filtered rows, flat) ---- */
    function csvCell(s) { s = (s == null ? '' : String(s)); if (/[",\n]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"'; return s; }
    if (exportEl) exportEl.addEventListener('click', function () {
      var headers = ['Cost', 'Price', 'Title', 'Image', 'Product & Material', 'Color', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'o/s', 'Other', 'Other detail', 'Variants w/ inv', 'Total qty'];
      var lines = [headers.map(csvCell).join(',')];
      filtered().slice().sort(cmp).forEach(function (r) {
        var imgSrc = (r.imgHtml.match(/src="([^"]+)"/) || [])[1] || '';
        lines.push([r.costStr, r.priceStr, r.title, imgSrc, r.material, r.color, r.xs, r.sm, r.md, r.lg, r.xl, r.xxl, r.os, r.other, (r.otherDetail || '').trim(), r.vars, r.total].map(csvCell).join(','));
      });
      var d = new Date(), iso = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
      var blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = 'ats-report-' + iso + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(function () { URL.revokeObjectURL(a.href); }, 1000);
    });
  }

  function boot() { document.querySelectorAll('.fs-ats').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
